import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { Button } from "@shared/src/components/ui/button";
import { FaRegCheckCircle } from "react-icons/fa";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useDisconnect } from "wagmi";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { TextWithCopy } from "@shared/src/components/common/misc/TextWithCopy";
import { processReferralAfterSignup } from "@shared/src/utils/referral";
import { cookieManager } from "@shared/src/lib/auth/cookieManager";
import UsernameDialog from "@shared/src/components/modules/dashboard/UsernameDialog";

interface AccountLinkingViewProps {
  onOpenChange: (open: boolean) => void;
}

function AccountLinkingContainer({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col items-center justify-center h-full gap-4">{children}</div>;
}

export function AccountLinkingView() {
  const {
    accountLinkingStatus,
    setAccountLinkingStatus,
    isExistingAccount,
    setIsExistingAccount,
    tempAuth,
    setSelectedView,
    closeAndResetAuthModal,
    resetAuthModal,
    signOut,
    activeAccount,
  } = useAuthStore();
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const { disconnectAsync } = useDisconnect();
  const setModalOpen = useAuthStore(state => state.setModalOpen);
  console.log("in account linking view", accountLinkingStatus);

  const router = useRouter();

  const handleUsernameChanged = async (username: string) => {
    const modal = useModalStore.getState();

    // Existing tracking code
    useHostStore
      .getState()
      .analytics?.trackSignupCompletion(
        activeAccount?.address || "unknown",
        username,
        activeAccount?.authParams.type || "unknown",
      );

    modal.showModal({
      type: "success",
      title: "Account created Successfully!",
      description: "You can now use your account to access all features on iExchange.",
      buttonClick: async () => {
        modal.closeModal();
        closeAndResetAuthModal();
      },
    });
  };

  const handleSignUp = async () => {
    if (!tempAuth) throw "Sign in not attempted";

    setIsExistingAccount(false);

    cookieManager().setAuthToken(tempAuth.jwt, tempAuth.address);

    // Process referral right after account is activated
    await processReferralAfterSignup();

    setIsProfileSettingsOpen(true);
  };

  const handleLinkExisting = async () => {
    await disconnectAsync();
    setIsExistingAccount(true);
    setAccountLinkingStatus("linking");
    // Switch back to social view for linking
    setSelectedView("social");
  };

  const handleGoToDashboard = () => {
    router.push("/dashboard");
    setModalOpen(false);
  };

  switch (accountLinkingStatus) {
    case "initial":
      if (!tempAuth?.connectorIcon) throw new Error("No connector icon found");
      return (
        <AccountLinkingContainer>
          <div className="rounded-xl  border-2 border-border-tertiary p-4">
            <img src={tempAuth.connectorIcon} alt="Auth Method" width={80} height={80} />
          </div>
          <div className="text-center">
            <p className="text-text-primary text-large font-extrabold mb-2">Sign Up</p>
            <p className=" px-4 text-text-tertiary !font-normal text-small-2">
              This {tempAuth.type === "eoa" ? "wallet (" + tempAuth.address + ")" : "account"} is not signed up on
              iExchange. Create a new Account or Link to existing account.{" "}
            </p>
          </div>
          <div className="flex flex-col items-center gap-4 w-full mt-4">
            <div className="flex flex-col items-center gap-4 w-full">
              <Button onClick={handleSignUp} variant="tertiary" size="lg" radius="full" className="max-w-fit px-16">
                Create a new account
              </Button>
              <div className="flex items-center gap-4 w-full">
                <div className="border-b border-border-tertiary w-full"></div>
                <p className="text-text-tertiary text-small-2">or</p>
                <div className="border-b border-border-tertiary w-full"></div>
              </div>
              <Button
                onClick={handleLinkExisting}
                variant="primary"
                size="lg"
                radius="full"
                className="max-w-fit px-16"
              >
                Link to existing account
              </Button>
            </div>
          </div>
          <UsernameDialog
            isOpen={isProfileSettingsOpen}
            onOpenChange={setIsProfileSettingsOpen}
            onUsernameChanged={handleUsernameChanged}
            title="Create Profile"
            preventClose
            description="Choose a unique username for your profile. This will be visible to other users."
          />
        </AccountLinkingContainer>
      );

    case "success":
      return (
        <AccountLinkingContainer>
          <div className="flex flex-col items-center gap-8 w-full">
            <div className="bg-surfaces-bg-secondary-darker rounded-full p-4">
              <FaRegCheckCircle size={28} className="text-icons-icon-success" />
            </div>
            {!isExistingAccount ? (
              <>
                <div className="text-center">
                  <p className="text-text-primary text-h6 font-semibold mb-2">
                    {accountLinkingStatus === "success"
                      ? "Account Linked Successfully"
                      : "Account created Successfully"}
                    !
                  </p>
                </div>
                <div className="flex justify-center">
                  <Button onClick={handleGoToDashboard} variant="tertiary" size="lg" radius="full" className="px-16">
                    {accountLinkingStatus === "success" ? "Continue with newly linked account" : "Go to Dashboard"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-text-primary text-h6 font-semibold mb-2">Accounts Linked Successfully!</p>
                </div>
                <div className="flex flex-col justify-center gap-4">
                  <Button
                    onClick={() => {
                      closeAndResetAuthModal();
                    }}
                    variant="tertiary"
                    size="lg"
                    radius="full"
                    width="full"
                    className="flex-col items-center px-16"
                  >
                    Continue with your existing Account{" "}
                    <span>
                      (<TextWithCopy text={activeAccount?.address || ""} />)
                    </span>
                  </Button>
                  <div className="flex items-center gap-4 w-full">
                    <div className="border-b border-border-tertiary w-full"></div>
                    <p className="text-text-tertiary text-small-2">or</p>
                    <div className="border-b border-border-tertiary w-full"></div>
                  </div>
                  <Button
                    onClick={() => {
                      signOut(disconnectAsync);
                      resetAuthModal();
                      setModalOpen(true);
                    }}
                    variant="tertiary"
                    size="lg"
                    radius="full"
                    width="full"
                    className="px-16 flex-col"
                  >
                    Sign in with your new account;
                    <span>
                      (<TextWithCopy text={tempAuth?.address || ""} />)
                    </span>
                  </Button>
                </div>
              </>
            )}
          </div>
        </AccountLinkingContainer>
      );

    default:
      return null;
  }
}
